import React, { useState, useEffect } from "react";
import ct from "class-types.macro";
import { cls } from "cls";
import { logException } from "@wss/error-tracking";
import Details from "~/ProductDetails/StickyHeader/Details";
import Navigation from "./Navigation";
import Pricing from "~/ProductDetails/StickyHeader/Pricing";
import AddToCartButton from "./AddToCartButton";
import QuantityDropdown from "./QuantityDropdown";
import LinkButton from "./LinkButton";
import { User } from "~/common/Store/models/User";
import connectUserAndChat from "~/common/Store/connectUserAndChat";
import { ProductDetailPageResult } from "~/ProductDetails/ProductDetailPageResult";
import shouldDisplayBelowMapPrice from "~/common/Product/shouldDisplayBelowMapPrice";
import { getTotalRatings } from "~/common/Reviews/CustomerReviewHelper";
import { CustomerReviews } from "~/common/Reviews/reviewModels";
import getLowestDisplayPrice from "~/common/Product/getLowestDisplayPrice";
import getMinBuyForQuantityDropdown from "../Pricing/AddToCart/getMinBuyForQuantityDropdown";
import getPriceForGoogleView from "../Pricing/getPriceForGoogleView";

interface Props {
  product: ProductDetailPageResult;
  quantity: string;
  hasQuantitySelect: boolean;
  onQuantityChanged: (value: string) => void;
  show: boolean;
  collapsed: boolean;
  onClose: () => void;
  setAddToCartSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  addToCartSuccess: boolean;
  isGoogleView?: boolean;
  alreadyInCart?: boolean;
  user: User;
  customerReviews: CustomerReviews;
  minimalPageVersion?: boolean;
}

// Do not use this component as a reference - it contains hacks to make things work
const StickyHeader = connectUserAndChat(
  ({
    product,
    quantity,
    hasQuantitySelect,
    onQuantityChanged,
    show,
    collapsed,
    onClose,
    setAddToCartSuccess,
    addToCartSuccess,
    isGoogleView = false,
    alreadyInCart = false,
    user,
    customerReviews,
    minimalPageVersion = false,
  }: Props) => {
    const {
      itemNumber,
      title,
      price,
      price: {
        minimumAdvertisedPriceProperties: { type: mapType },
      },
      unitOfMeasure = "Each",
      isInStock = true,
      hasRequiredAccessories,
      isReplacementEligible = false,
      isCustomizable = false,
      customizationRequired = false,
      startBuy,
      minimumBuy = 1,
      mustBuy = 0,
      productImages,
      grouping = false,
      showBelowMinimumAdvertisedPriceForLoggedInCustomers: showMap,
      showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers:
        showPlatPlusMap,
      customizationUrl,
      customerExclusivity,
      originalId,
      isVirtualPart,
      groupingAttributes: { isGroupMaster },
      isRequestAQuoteEnabled,
    } = product;

    const selectedQuantity = Number(quantity);

    const mapStyle = mapType?.trim();
    const isOutletItem = !!product.outletAttributes?.outletProductId || false;

    const image =
      productImages.find((i) => i.isPrimaryDetailImage)?.thumbnail ||
      productImages[0]?.thumbnail ||
      "";

    const reviewCount = getTotalRatings(
      customerReviews.productReviewDetails,
    ).length;

    const placeAtTop = !window?.document?.querySelector(
      "#logged-in-as-warning-banner",
    );

    const [error, setError] = useState<string | null>(null);
    const [isAdded, setIsAdded] = useState(false);
    const [timer, setTimer] = useState(0);

    useEffect(() => setIsAdded(addToCartSuccess), [addToCartSuccess]);

    const links = [
      {
        area: "#details-group",
        text: "Details",
        show: () => !!document.querySelector("#details-group"),
        onClick: () => clearSidebarHighlight("details-group-headline"),
      },
      {
        area: "#resources-group",
        text: "Resources & Downloads",
        shortText: "Resources",
        show: () => !!document.querySelector("#resources-group"),
        onClick: () => navigateToSidebar("resources-group"),
      },
      {
        area: "#specifications-group",
        text: "Specifications & Dimensions",
        shortText: "Specifications",
        show: () => !!document.querySelector("#specifications-group"),
        onClick: () => navigateToSidebar("specifications-group"),
      },
      {
        area: "#customerQA",
        title: "Customer Q&A",
        text: "Questions & Answers",
        show: () =>
          !!document.querySelector("#customerQA") && !isReplacementEligible,
        onClick: () => clearSidebarHighlight("customerQA"),
      },
      {
        area: "#compare",
        text: "Compare",
        show: () => !!document.querySelector("#compare"),
        onClick: () =>
          clearSidebarHighlight("compare-to-other-products-heading"),
      },
      {
        area: "#review-section",
        title: "Customer Reviews",
        text: (
          <>
            Reviews
            <span className="text-gray-700 quicklinks-banner__review-count">
              ({reviewCount})
            </span>
          </>
        ),
        show: () =>
          !!document.querySelector("#review-section") && reviewCount > 0,
        onClick: () => clearSidebarHighlight("review-section"),
      },
    ];
    useEffect(() => {
      if (collapsed) {
        document.getElementById("sh-show-quick-links")?.focus();
      } else if (!collapsed) {
        document.getElementById("sh-hide-quick-links")?.focus();
      }
    }, [collapsed]);

    const handleAtcClicked = () => {
      const updatedStartBuy = getMinBuyForQuantityDropdown({
        minBuy: startBuy,
        mustBuy,
      });

      // validations
      switch (true) {
        case selectedQuantity < updatedStartBuy:
          setError(` Please enter a quantity of ${updatedStartBuy} or more.`);
          return;
        case selectedQuantity >= updatedStartBuy &&
          selectedQuantity % mustBuy !== 0:
          setError(` Please enter a quantity as a multiple of ${mustBuy}.`);
          return;
        default:
          break;
      }

      setError(null);
      const buyButton = document.querySelector<HTMLButtonElement>("#buyButton");
      if (buyButton) {
        buyButton.click();
      }
    };

    const handleQtyChanged = ({
      target: { value },
    }: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      onQuantityChanged(value);

    const handleMouseEnter = () => {
      window.clearTimeout(timer);
      setTimer(0);
    };
    const handleMouseLeave = () => {
      setTimer(
        window.setTimeout(() => {
          setAddToCartSuccess(false);
        }, 10000),
      );
    };

    const updateDisplayPriceWithQtyDiscount = (
      hasQuantityDiscount: boolean,
      quantityDiscountAmount: { price: number } | null | undefined,
      displayPrice: number,
    ): number => {
      if (
        hasQuantityDiscount &&
        quantityDiscountAmount &&
        typeof quantityDiscountAmount.price === "number"
      ) {
        if (displayPrice > quantityDiscountAmount.price) {
          return quantityDiscountAmount.price;
        }
      }
      return displayPrice;
    };

    let showPrice = !isReplacementEligible;
    let displayPrice: number;
    let priceExtraText = "";

    if (isOutletItem) {
      displayPrice = price.price;
    } else if (
      isGoogleView &&
      !user.registered &&
      product.maximumCustomizationPrice &&
      customizationRequired
    ) {
      displayPrice = product.maximumCustomizationPrice;
    } else if (isGoogleView && !user.registered) {
      /// Checking to see if it's a google view and the user is not registered, then we grab a different price than above

      displayPrice = getLowestDisplayPrice(product, user, false, false);
      displayPrice = getPriceForGoogleView({
        minimumBuy,
        mustBuy,
        price: displayPrice,
      });
    } else {
      displayPrice = getLowestDisplayPrice(product, user, false, false);
    }

    if (customizationRequired && isGoogleView && !user.registered) {
      priceExtraText = "From";
    } else if (customizationRequired) {
      priceExtraText = "Starting At";
    }

    const shouldDisplaySalePrice = () => {
      return (
        price.displaySalePrice &&
        price.salePrice < price.minimumAdvertisedPriceProperties.price
      );
    };

    if (isInStock) {
      if (product.groupingAttributes.isGroupMaster) {
        showPrice = false;
      }

      if (mapStyle) {
        showPrice = false;
        if (shouldDisplayBelowMapPrice(product, user) && mapStyle !== "G") {
          showPrice = true;
          if (user.registered && user.platinum.enabled && showPlatPlusMap) {
            priceExtraText = "Platinum Member Price";
          } else if (user.registered && user.hasWebPlus && showPlatPlusMap) {
            priceExtraText = "Plus Member Price";
          } else if (user.registered && showMap) {
            priceExtraText = `Custom Quote for ${
              user.companyName || user.fullName
            }`;
          }
        } else if (mapStyle === "G") {
          priceExtraText = "";
          showPrice = !isGoogleView;
        } else if (shouldDisplaySalePrice()) {
          displayPrice = price.salePrice;
          showPrice = !isGoogleView;
        } else if (!isGoogleView) {
          displayPrice = price.price;
        }
      }
    } else {
      showPrice = false;
    }

    const shouldShowUnitOfMeasure = // whether we want the pricebox to show the unit of measure
      showPrice && isGoogleView && !customizationRequired && !user.registered;
    const adjustedUnitOfMeasure = // change what the unit of measure is based on the conditions
      minimumBuy <= 1 && mustBuy <= 1 && isGoogleView ? unitOfMeasure : "";

    const getAtcArea = () => {
      let blocked = customerExclusivity?.disallowNonMemberToPurchase || false;

      if (blocked) {
        const { allowPlatinumToPurchase, allowWebstaurantPlusToPurchase } =
          customerExclusivity;

        if (allowPlatinumToPurchase && user.platinum.enabled) {
          blocked = false;
        } else if (allowWebstaurantPlusToPurchase && user.hasWebPlus) {
          blocked = false;
        }
      }

      if (!isInStock || isReplacementEligible || blocked) {
        return false;
      }

      if (customizationRequired && (isCustomizable || customizationUrl)) {
        return (
          <LinkButton
            id="sticky-customize-button"
            href={customizationUrl || `/customized-products/${itemNumber}/`}
            className="btn btn-cart"
          >
            Customize
          </LinkButton>
        );
      }

      if (hasRequiredAccessories) {
        return (
          <LinkButton className="btn btn-primary" href="#priceBox">
            Configure
          </LinkButton>
        );
      }

      if (
        product.groupingAttributes.isGroupMaster ||
        (mapStyle === "G" && isGoogleView) ||
        (mapStyle &&
          mapStyle !== "G" &&
          !shouldDisplayBelowMapPrice(product, user) &&
          !shouldDisplaySalePrice())
      ) {
        return false;
      }

      let component;
      if (!isOutletItem) {
        if (!product.groupingAttributes.isGroupMaster && hasQuantitySelect) {
          component = (
            <div className="form pull-left">
              <div className="form__control-container">
                <QuantityDropdown
                  className="form__control"
                  value={selectedQuantity}
                  min={minimumBuy}
                  step={mustBuy}
                  onChange={handleQtyChanged}
                />
                <label className="form__label">Quantity</label>
                <i className="icon-down-micro form__control-icon form__control-icon--lg" />
              </div>
            </div>
          );
        } else {
          component = (
            <input
              aria-label="Quantity"
              aria-describedby="sh-quantity-error"
              type="number"
              className="quantity"
              value={quantity}
              onChange={handleQtyChanged}
            />
          );
        }
      } else if (alreadyInCart) {
        return (
          <button className="btn" disabled>
            Already in your cart
          </button>
        );
      }

      return (
        <>
          {component}
          <AddToCartButton onClick={() => handleAtcClicked()} error={error} />
        </>
      );
    };

    const clearSidebarHighlight = (id: string) => {
      const setTabIndexAndFocus = (element: HTMLElement | null) => {
        if (element && !element.hasAttribute("tabindex")) {
          element.setAttribute("tabindex", "-1");
        }
        element?.focus();
      };

      const groupToFocus = document.getElementById(id);
      if (id !== "resources-group") {
        setTabIndexAndFocus(groupToFocus);
      } else {
        const resourcesHeader = document.getElementById(
          "resources-group-header",
        );
        setTabIndexAndFocus(resourcesHeader);
      }

      const highlightClassName = "quicklinks-banner__sidebar-group__highlight";
      const noHighlightClassName =
        "quicklinks-banner__sidebar-group__no-highlight";
      const elements = document.querySelectorAll(`.${highlightClassName}`);
      elements.forEach((element) => {
        element.classList.remove(highlightClassName);
        element.classList.add(noHighlightClassName);
      });
    };

    const navigateToSidebar = (id: string) => {
      clearSidebarHighlight(id);
      const groupToHighlight = document.getElementById(id);
      if (groupToHighlight) {
        groupToHighlight.classList.add(
          "quicklinks-banner__sidebar-group__highlight",
        );
        groupToHighlight.classList.remove(
          "quicklinks-banner__sidebar-group__no-highlight",
        );
        setTimeout(() => {
          groupToHighlight.classList.remove(
            "quicklinks-banner__sidebar-group__highlight",
          );
          groupToHighlight.classList.add(
            "quicklinks-banner__sidebar-group__no-highlight",
          );
        }, 3000);
      }
    };

    const handleBackToTopClick = () => {
      const backToTopLink = document?.getElementById("td");

      if (!backToTopLink) {
        const error = new Error(
          `Unable to scroll back to the top. Please ensure the element is in the DOM.`,
        );
        logException(error);
        throw error;
      }
      backToTopLink.scrollIntoView();
    };

    return (
      <>
        {show && (
          <div className="quicklinks-banner__group">
            {(collapsed && (
              <button
                id="sh-show-quick-links"
                type="button"
                className="quicklinks-banner__toggle is-visible"
                onClick={onClose}
              >
                <span className={ct("sr-only")}>Show Quick Links</span>
                <span className="icon-down-micro" aria-hidden="true" />
              </button>
            )) || (
              <div
                className={cls(
                  "quicklinks-banner is-visible",
                  user.registered ? "is-logged-in" : "is-logged-out",
                  placeAtTop && "top-0",
                )}
              >
                <div className="container relative">
                  <span className="quicklinks-banner__close">
                    <button
                      id="sh-hide-quick-links"
                      type="button"
                      className="close"
                      onClick={onClose}
                    >
                      <span className={ct("sr-only")}>Hide Quick Links</span>
                      <i className="icon-cancel" aria-hidden="true" />
                    </button>
                  </span>
                  <div className="quicklinks-banner__inner">
                    <div className="quicklinks-banner__left-content">
                      <Details
                        imgSrc={image}
                        rating={customerReviews.overallRating}
                        itemNumber={
                          isOutletItem ||
                          isVirtualPart ||
                          (grouping && isGroupMaster)
                            ? originalId
                            : itemNumber
                        }
                        title={title}
                        minimalPageVersion={minimalPageVersion}
                      />
                    </div>
                    <div className="quicklinks-banner__right-content">
                      {isRequestAQuoteEnabled ? (
                        <div className="quicklinks-banner__cart-block ml-1.5">
                          <div className="quicklinks-banner__addtocart-block">
                            <a className="btn btn-cart" href="/special-orders">
                              Request a Quote
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`quicklinks-banner__cart-block ${
                            isAdded ? "is-added" : ""
                          }`}
                        >
                          <div
                            className={`quicklinks-banner__addtocart-block ${
                              isAdded && user.registered
                                ? "is-added logged-in"
                                : ""
                            }`}
                          >
                            {showPrice ? (
                              <Pricing
                                price={displayPrice}
                                unitOfMeasure={
                                  shouldShowUnitOfMeasure
                                    ? adjustedUnitOfMeasure
                                    : unitOfMeasure
                                }
                                extraText={priceExtraText}
                              />
                            ) : null}
                            {getAtcArea()}
                          </div>
                          <div
                            className={`quicklinks-banner__cart-alert-block ${
                              isAdded && user.registered
                                ? "is-added logged-in"
                                : ""
                            }`}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                          >
                            <div className="amount">
                              <span className="unit">{`${quantity} item${
                                selectedQuantity > 1 ? "s" : ""
                              }`}</span>
                              {" added to your cart"}
                            </div>
                            <a href="/viewcart.cfm" className="btn btn-primary">
                              <i className="icon-shopping-cart pr-1" />
                              View Cart
                            </a>
                            <a
                              href="/shipping-billinginfo.cfm"
                              className="btn btn-orange"
                            >
                              <i className="icon-lock pr-1" />
                              Checkout
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="border-0 border-t border-gray-200 border-solid">
                  <div className="container flex items-center justify-between before:block after:block">
                    <Navigation>
                      <ul
                        className="list-none mb-0 mx-0"
                        aria-label="quick links"
                      >
                        {links
                          .filter((e) => e.show())
                          .map((e) => (
                            <Navigation.Button
                              key={e.area}
                              area={e.area}
                              title={e.title}
                              text={e.text}
                              shortText={e.shortText}
                              onClick={e.onClick}
                            />
                          ))}
                      </ul>
                    </Navigation>
                    {!minimalPageVersion && (
                      <button
                        type="button"
                        onClick={handleBackToTopClick}
                        className="quicklinks-banner__nav-item bg-transparent border-0 flex items-center py-2 mr-4 text-sm font-semibold text-gray-800 scroll-anchor hover:underline lg:mr-0"
                      >
                        <span className="mr-2">Back To Top</span>
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M6.6667 3.21869V11.3334H5.33336V3.21869L1.75736 6.79469L0.814697 5.85202L6.00003 0.666687L11.1854 5.85202L10.2427 6.79469L6.6667 3.21869Z"
                            fill="#334155"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  },
);

export default StickyHeader;
